import React, { useRef, useEffect, useState } from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Dimensions,
  Animated,
  Alert,
  Platform,
  Easing,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { LinearGradient } from "expo-linear-gradient";
import { Opportunity } from "../types";
import { format } from "date-fns";
import { useFavorites } from "../contexts/FavoritesContext";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { calculateOpportunityStatus } from "../utils/filterUtils";
import {
  shouldDisableAnimations,
  getAnimationDuration,
} from "../utils/performanceUtils";
import { PERFORMANCE_CONFIG } from "../config/performance.config";
import AuthRequiredModal from "./auth/AuthRequiredModal";

interface FeaturedOpportunityProps {
  opportunity: Opportunity;
  onPress: (id: string) => void;
}

const { width } = Dimensions.get("window");
const isTabletOrDesktop = width >= 768;
const isSmallMobile = width < 380;

// Color palette for light/dark mode
const colors = {
  light: {
    featuredCard: "#FFFFFF",
    text: "#1E293B",
    subtext: "#64748B",
    organizationText: "#64748B",
    infoText: "#64748B",
    infoBackground: "rgba(248, 250, 252, 0.7)",
    infoBorder: "rgba(226, 232, 240, 0.5)",
    typeIconBackground: "#FEF3C7",
    typeIconBorder: "#FDE68A",
    featuredBadgeBackground: "#FEF3C7",
    featuredBadgeText: "#D97706",
    infoButtonBackground: "rgba(100, 116, 139, 0.15)",
    infoButtonBorder: "rgba(100, 116, 139, 0.3)",
    favoriteButtonBackground: "rgba(248, 250, 252, 0.9)",
    ctaButton: "#F4F4F5",
    ctaButtonBorder: "#E4E4E7",
    ctaButtonText: "#272727",
    highlightText: "#059669",
    highlightBackground: "rgba(16, 185, 129, 0.1)",
    highlightBorder: "#059669",
    bookmarkActive: "#272727",
    bookmarkInactive: "#A1A1AA",
  },
  dark: {
    featuredCard: "#27272A",
    text: "#FAFAFA",
    subtext: "#D4D4D8",
    organizationText: "#D4D4D8",
    infoText: "#D4D4D8",
    infoBackground: "rgba(39, 39, 42, 0.7)",
    infoBorder: "rgba(58, 58, 61, 0.5)",
    typeIconBackground: "#2A2A2E",
    typeIconBorder: "#3A3A3D",
    featuredBadgeBackground: "#2A2A2E",
    featuredBadgeText: "#F59E0B",
    infoButtonBackground: "rgba(212, 212, 216, 0.15)",
    infoButtonBorder: "rgba(212, 212, 216, 0.3)",
    favoriteButtonBackground: "rgba(39, 39, 42, 0.9)",
    ctaButton: "#34343B",
    ctaButtonBorder: "#2D2D34",
    ctaButtonText: "#FAFAFA",
    highlightText: "#34D399",
    highlightBackground: "rgba(52, 211, 153, 0.1)",
    highlightBorder: "#34D399",
    bookmarkActive: "#FFFFFF",
    bookmarkInactive: "#71717A",
  },
};

const FeaturedOpportunity: React.FC<FeaturedOpportunityProps> = ({
  opportunity,
  onPress,
}) => {
  const { theme } = useTheme();
  const c = colors[theme];
  const { isAuthenticated } = useAuth();
  const { saveOpportunity, unsaveOpportunity, isSaved } = useFavorites();
  const [isFavorite, setIsFavorite] = useState(isSaved(opportunity.id));
  const [showAuthModal, setShowAuthModal] = useState(false);

  // Check if animations should be disabled
  const disableAnimations = shouldDisableAnimations();

  // Clean animations
  const fadeAnim = useRef(
    new Animated.Value(disableAnimations ? 1 : 0),
  ).current;
  const slideAnim = useRef(
    new Animated.Value(disableAnimations ? 0 : 30),
  ).current;
  const favoriteAnim = useRef(new Animated.Value(1)).current;

  // Simplified rainbow border animation - use discrete steps instead of continuous interpolation
  const rainbowStep = useRef(new Animated.Value(0)).current;

  // Simplified glow animation - DISABLED for performance
  // const glowIntensity = useRef(new Animated.Value(0.7)).current;

  useEffect(() => {
    if (disableAnimations) {
      // Skip animations on low-end devices
      return;
    }

    // Entrance animation - synchronized with OpportunityCard timing
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 400, // Match OpportunityCard duration
        useNativeDriver: true,
      }),
      Animated.spring(slideAnim, {
        toValue: 0,
        tension: 40,
        friction: 8,
        useNativeDriver: true,
      }),
    ]).start();

    // Optimized rainbow border - step through 6 colors instead of 11, slower timing
    const rainbowAnimation = Animated.loop(
      Animated.timing(rainbowStep, {
        toValue: 6,
        duration: getAnimationDuration(24000), // Even slower = less frequent updates
        useNativeDriver: false,
        // Add easing to make it smoother
        easing: Easing.linear,
      }),
    );

    // Only start rainbow animation after entrance animation
    setTimeout(() => {
      if (!disableAnimations) {
        rainbowAnimation.start();
      }
    }, 400); // Reduced from 800 to match entrance duration

    // Simplified glow - DISABLED for performance
    // Animated.loop(
    //   Animated.sequence([
    //     Animated.timing(glowIntensity, {
    //       toValue: 0.9,
    //       duration: 4000, // Longer duration = fewer updates
    //       useNativeDriver: false,
    //     }),
    //     Animated.timing(glowIntensity, {
    //       toValue: 0.7,
    //       duration: 4000,
    //       useNativeDriver: false,
    //     }),
    //   ])
    // ).start();

    return () => {
      rainbowAnimation.stop();
    };
  }, [disableAnimations]);

  useEffect(() => {
    setIsFavorite(isSaved(opportunity.id));
  }, [opportunity.id]);

  const handleToggleFavorite = async (e: any) => {
    e.stopPropagation();

    if (!isAuthenticated) {
      setShowAuthModal(true);
      return;
    }

    // Only animate if animations are enabled
    if (!disableAnimations) {
      Animated.sequence([
        Animated.timing(favoriteAnim, {
          toValue: 0.7,
          duration: 100,
          useNativeDriver: true,
        }),
        Animated.spring(favoriteAnim, {
          toValue: 1.1,
          tension: 300,
          friction: 3,
          useNativeDriver: true,
        }),
        Animated.spring(favoriteAnim, {
          toValue: 1,
          tension: 300,
          friction: 5,
          useNativeDriver: true,
        }),
      ]).start();
    }

    if (isFavorite) {
      await unsaveOpportunity(opportunity.id);
    } else {
      await saveOpportunity(opportunity.id);
    }

    setIsFavorite(!isFavorite);
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return null; // Return null for conditional rendering
    try {
      return format(new Date(dateString), "MMM d, yyyy");
    } catch (error) {
      console.error("Error formatting date:", dateString, error);
      return null; // Return null for invalid dates
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type?.toLowerCase()) {
      case "hackathon":
        return "code-slash";
      case "competition":
        return "trophy";
      case "grant":
        return "cash";
      case "scholarship":
        return "school";
      case "conference":
        return "people";
      case "workshop/programs":
        return "construct";
      case "internship":
        return "briefcase";
      default:
        return "star";
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Open":
        return "#10B981";
      case "Coming Soon":
        return "#F59E0B";
      case "Ongoing":
        return "#3B82F6";
      case "Closed":
        return "#EF4444";
      default:
        return "#71717A";
    }
  };

  const getStatusLabel = (status: string) => {
    return status ? status.toUpperCase() : "OPEN";
  };

  const calculatedStatus = calculateOpportunityStatus(opportunity);
  const primaryType = Array.isArray(opportunity.type)
    ? opportunity.type[0]
    : opportunity.type.split(",")[0]?.trim();

  // Pre-calculate rainbow colors to avoid interpolation in render
  const rainbowColors = [
    "#FF6B6B", // Red
    "#FFD93D", // Yellow
    "#6BCF7F", // Green
    "#4ECDC4", // Teal
    "#45B7D1", // Blue
    "#B794F6", // Purple
    "#FF6B6B", // Back to Red
  ];

  return (
    <Animated.View
      style={[
        styles.featuredWrapper,
        {
          opacity: fadeAnim,
          transform: [{ translateY: slideAnim }],
        },
      ]}
      data-oid="3vvovw1"
    >
      {/* Static glow effect - no animation for better performance */}
      {!disableAnimations && PERFORMANCE_CONFIG.visuals.glowEnabled && (
        <View
          style={[
            styles.glowContainer,
            { opacity: PERFORMANCE_CONFIG.visuals.glowOpacity },
          ]}
          data-oid="s9krozb"
        >
          {Platform.OS === "web" ? (
            <View style={[styles.glowEffect]} data-oid="q9bctjs" />
          ) : (
            <LinearGradient
              colors={["#F59E0B", "#FFD93D", "#F59E0B"]}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 1 }}
              style={styles.glowGradient}
              data-oid="ro:ol-b"
            />
          )}
        </View>
      )}

      {/* Rainbow Border Container */}
      <View style={styles.rainbowBorderContainer} data-oid="g0abt0s">
        {/* Animated Rainbow Border - disable on low-end devices */}
        {!disableAnimations ? (
          <Animated.View
            style={[
              styles.rainbowBorder,
              {
                borderColor: rainbowStep.interpolate({
                  inputRange: [0, 1, 2, 3, 4, 5, 6],
                  outputRange: rainbowColors,
                }),
              },
            ]}
            data-oid="dud4_w7"
          />
        ) : (
          <View
            style={[styles.rainbowBorder, { borderColor: "#F59E0B" }]}
            data-oid="c8by3dw"
          />
        )}

        {/* Clean Card Content */}
        <TouchableOpacity
          style={[styles.featuredCard, { backgroundColor: c.featuredCard }]}
          onPress={() => onPress(opportunity.id)}
          activeOpacity={0.95}
          data-oid="vwswzmb"
        >
          {/* Header */}
          <View style={styles.cardHeader} data-oid="6_e6u9m">
            <View style={styles.headerLeft} data-oid="szgayqs">
              <View
                style={[
                  styles.featuredBadge,
                  { backgroundColor: c.featuredBadgeBackground },
                ]}
                data-oid="8skv8iu"
              >
                <Ionicons
                  name="star"
                  size={14}
                  color="#F59E0B"
                  data-oid="6p_4y4e"
                />

                <Text
                  style={[
                    styles.featuredBadgeText,
                    { color: c.featuredBadgeText },
                  ]}
                  data-oid="2elt5cc"
                >
                  Spotlight Sunday
                </Text>
              </View>
            </View>

            <View
              style={[
                styles.statusBadge,
                {
                  backgroundColor: getStatusColor(calculatedStatus || 'Open') + "15",
                  borderColor: getStatusColor(calculatedStatus || 'Open') + "30",
                },
              ]}
              data-oid="fl7a-:l"
            >
              <View
                style={[
                  styles.statusDot,
                  { backgroundColor: getStatusColor(calculatedStatus || 'Open') },
                ]}
                data-oid="vctbrs3"
              />

              <Text
                style={[
                  styles.statusText,
                  { color: getStatusColor(calculatedStatus || 'Open') },
                ]}
                data-oid="llpnmoz"
              >
                {getStatusLabel(calculatedStatus || 'Open')}
              </Text>
            </View>
          </View>

          {/* Content */}
          <View style={styles.cardContent} data-oid=".q5x8h7">
            <Text
              style={[styles.title, { color: c.text }]}
              numberOfLines={2}
              data-oid="fn7d:4:"
            >
              {opportunity.title}
            </Text>

            <View style={styles.organizationContainer} data-oid="gb4e7nr">
              <Ionicons
                name="business-outline"
                size={12}
                color={c.subtext}
                data-oid=".l62pr8"
              />

              <Text
                style={[styles.organization, { color: c.organizationText }]}
                numberOfLines={1}
                data-oid="1k3svl0"
              >
                {opportunity.organization}
              </Text>
            </View>

            <Text
              style={[styles.description, { color: c.subtext }]}
              numberOfLines={3}
              data-oid="s7nv4:y"
            >
              {opportunity.description}
            </Text>

            {/* Enhanced Info section */}
            <View
              style={[
                styles.infoGrid,
                {
                  backgroundColor: c.infoBackground,
                  borderColor: c.infoBorder,
                },
              ]}
              data-oid=":bu4gbb"
            >
              <View style={styles.infoRow} data-oid="7mkgu3p">
                {/* Only show start date if it exists and is valid */}
                {formatDate(opportunity.startDate) && (
                  <View style={styles.infoItem} data-oid="21l1.hf">
                    <Ionicons
                      name="calendar-outline"
                      size={14}
                      color={c.infoText}
                      data-oid="s.fy.99"
                    />

                    <Text
                      style={[styles.infoText, { color: c.infoText }]}
                      data-oid="xpj:5zg"
                    >
                      {formatDate(opportunity.startDate)}
                    </Text>
                  </View>
                )}
              </View>

              {/* Only show end date and location row if there's content to show */}
              {(formatDate(opportunity.endDate) || opportunity.location) && (
                <View style={styles.infoRow} data-oid="jz_5fi0">
                  {!isSmallMobile && formatDate(opportunity.endDate) && (
                    <View style={styles.infoItem} data-oid=":7r8tfl">
                      <Ionicons
                        name="time-outline"
                        size={14}
                        color={c.infoText}
                        data-oid="0d8ymvr"
                      />

                      <Text
                        style={[styles.infoText, { color: c.infoText }]}
                        data-oid="bx:hq1b"
                      >
                        Ends: {formatDate(opportunity.endDate)}
                      </Text>
                    </View>
                  )}

                  {opportunity.location && (
                    <View
                      style={[
                        styles.infoItem,
                        isSmallMobile && styles.infoItemFullWidth,
                      ]}
                      data-oid="lp:hkxu"
                    >
                      <Ionicons
                        name="location-outline"
                        size={14}
                        color={c.infoText}
                        data-oid="2vulcpc"
                      />

                      <Text
                        style={[styles.infoText, { color: c.infoText }]}
                        numberOfLines={1}
                        ellipsizeMode="tail"
                        data-oid="wfr-ypo"
                      >
                        {opportunity.location}
                      </Text>
                    </View>
                  )}
                </View>
              )}

              {/* Show location separately on mobile if no endDate but has location */}
              {isSmallMobile &&
                !formatDate(opportunity.endDate) &&
                opportunity.location && (
                  <View style={styles.infoRow} data-oid="gdshcf3">
                    <View
                      style={[styles.infoItem, styles.infoItemFullWidth]}
                      data-oid="aclj84_"
                    >
                      <Ionicons
                        name="location-outline"
                        size={14}
                        color={c.infoText}
                        data-oid="jc96.q5"
                      />

                      <Text
                        style={[styles.infoText, { color: c.infoText }]}
                        numberOfLines={1}
                        ellipsizeMode="tail"
                        data-oid="24n4j46"
                      >
                        {opportunity.location}
                      </Text>
                    </View>
                  </View>
                )}

              {opportunity.eligibility && (
                <View
                  style={[
                    styles.highlightInfo,
                    {
                      backgroundColor: c.highlightBackground,
                      borderLeftColor: c.highlightBorder,
                    },
                  ]}
                  data-oid="v5lk.9p"
                >
                  <Ionicons
                    name="checkmark-circle-outline"
                    size={14}
                    color={c.highlightText}
                    data-oid="b1uk:q4"
                  />

                  <Text
                    style={[styles.highlightText, { color: c.highlightText }]}
                    numberOfLines={2}
                    data-oid="sdl9ghw"
                  >
                    {opportunity.eligibility}
                  </Text>
                </View>
              )}
            </View>

            {/* Bottom row */}
            <View style={styles.bottomRow} data-oid=":_qbb4s">
              <View style={styles.typeContainer} data-oid="4rues3v">
                <View
                  style={[
                    styles.typeIconContainer,
                    {
                      backgroundColor: c.typeIconBackground,
                      borderColor: c.typeIconBorder,
                    },
                  ]}
                  data-oid="ltyc1sm"
                >
                  <Ionicons
                    name={getTypeIcon(primaryType)}
                    size={16}
                    color="#F59E0B"
                    data-oid="3p370oj"
                  />
                </View>
                <Text
                  style={[styles.typeText, { color: c.subtext }]}
                  numberOfLines={1}
                  data-oid="tw_.vrq"
                >
                  {primaryType}
                </Text>
              </View>

              <View style={styles.footerActions} data-oid="wp_cfsx">
                <TouchableOpacity
                  style={styles.favoriteButton}
                  onPress={handleToggleFavorite}
                  data-oid="gtrb15v"
                >
                  <Animated.View
                    style={{ transform: [{ scale: favoriteAnim }] }}
                    data-oid="n-w.3lz"
                  >
                    <Ionicons
                      name={isFavorite && isAuthenticated ? "bookmark" : "bookmark-outline"}
                      size={22}
                      color={isFavorite && isAuthenticated ? c.bookmarkActive : c.bookmarkInactive}
                      data-oid="du_t99n"
                    />
                  </Animated.View>
                </TouchableOpacity>

                <TouchableOpacity
                  style={[
                    styles.ctaButton, 
                    { 
                      backgroundColor: c.ctaButton,
                      borderColor: c.ctaButtonBorder,
                    }
                  ]}
                  onPress={() => onPress(opportunity.id)}
                  activeOpacity={0.8}
                  data-oid="a:8awz."
                >
                  <Text
                    style={[styles.ctaText, { color: c.ctaButtonText }]}
                    data-oid="10v9t-u"
                  >
                    Explore
                  </Text>
                  <Ionicons
                    name="chevron-forward"
                    size={14}
                    color={c.ctaButtonText}
                    data-oid="sdqb3b."
                  />
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </TouchableOpacity>
      </View>

      {/* Auth Required Modal */}
      <AuthRequiredModal
        visible={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        featureName="saved opportunities"
      />
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  featuredWrapper: {
    width: "100%",
    marginVertical: 16,
    marginHorizontal: 12, // Reduced horizontal margin for smaller glow
    position: "relative",
    alignSelf: "center", // Stretch to fill available space within margins
  },
  // Static glow styles for performance - smaller and more subtle
  glowContainer: {
    position: "absolute",
    top: -8, // Reduced from -20 for smaller glow
    left: -8, // Reduced from -20 for smaller glow
    right: -8, // Reduced from -20 for smaller glow
    bottom: -8, // Reduced from -20 for smaller glow
    borderRadius: 24, // Reduced from 40 for smaller glow
    zIndex: 0,
    // opacity is set dynamically via PERFORMANCE_CONFIG
  },
  glowEffect: {
    flex: 1,
    borderRadius: 24, // Reduced from 40 for smaller glow
    backgroundColor: "#F59E0B", // Static golden color to match featured theme
    ...Platform.select({
      ios: {
        shadowOffset: { width: 0, height: 0 },
        shadowRadius: 12, // Reduced from 25 for more subtle effect
        shadowOpacity: 0.15, // Reduced from 0.3 for more subtle effect
      },
      web: {
        filter: "blur(12px)", // Reduced from 25px for more subtle effect
        boxShadow: "0 0 24px rgba(245, 158, 11, 0.2)", // Reduced size and opacity
      },
      android: {
        elevation: 0, // No elevation on Android, using opacity instead
      },
    }),
  },
  glowGradient: {
    flex: 1,
    borderRadius: 40,
  },
  rainbowBorderContainer: {
    position: "relative",
    borderRadius: 20,
    zIndex: 1,
  },
  rainbowBorder: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: 20,
    borderWidth: 4,
  },
  featuredCard: {
    borderRadius: 16,
    overflow: "hidden",
    position: "relative",
    minHeight: isSmallMobile ? 200 : 240,
    margin: 4,
    zIndex: 2,
  },
  cardHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 20,
    paddingBottom: 16,
  },
  headerLeft: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  featuredBadge: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 16,
    gap: 6,
  },
  featuredBadgeText: {
    fontSize: 12,
    fontWeight: "800",
    fontFamily: "Inter-ExtraBold",
    letterSpacing: 0.5,
    textTransform: "uppercase",
  },
  infoButton: {
    padding: 8,
    borderRadius: 10,
    borderWidth: 1,
    elevation: 2,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    zIndex: 10,
    ...Platform.select({
      web: {
        cursor: "pointer",
        transition: "all 0.2s ease",
        ":hover": {
          transform: "scale(1.1)",
        },
      },
    }),
  },

  cardContent: {
    padding: 20,
    paddingTop: 0,
  },
  title: {
    fontSize: isSmallMobile ? 18 : 20,
    fontWeight: "800",
    lineHeight: isSmallMobile ? 22 : 26,
    fontFamily: "Inter-ExtraBold",
    marginBottom: 8,
    letterSpacing: -0.3,
  },
  organizationContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    marginBottom: 12,
  },
  organization: {
    fontSize: 14,
    fontFamily: "Inter-SemiBold",
    fontWeight: "600",
    flex: 1,
  },
  description: {
    fontSize: 13,
    lineHeight: 18,
    fontFamily: "Inter-Regular",
    marginBottom: 16,
  },
  quickInfoContainer: {
    backgroundColor: "rgba(248, 250, 252, 0.7)",
    borderRadius: 12,
    padding: 12,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: "rgba(226, 232, 240, 0.5)",
  },
  quickInfo: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  infoItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    flex: 1,
    minWidth: 0,
  },
  infoText: {
    fontSize: 12,
    fontFamily: "Inter-SemiBold",
    fontWeight: "600",
    flex: 1,
    flexShrink: 1,
  },
  statusContainer: {
    alignItems: "flex-end",
  },
  statusIndicator: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: "rgba(255, 255, 255, 0.9)",
  },
  bottomRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  typeContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    flex: 1,
    marginRight: 16,
  },
  typeIconContainer: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 1,
  },
  typeText: {
    fontSize: 13,
    fontFamily: "Inter-SemiBold",
    fontWeight: "600",
    flex: 1,
  },
  ctaButton: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 8,
    borderWidth: 1,
  },
  ctaText: {
    fontSize: 13,
    fontWeight: "600",
    marginRight: 4,
    fontFamily: "Inter-SemiBold",
  },
  infoGrid: {
    borderRadius: 12,
    padding: 12,
    marginBottom: 16,
    borderWidth: 1,
    gap: 8,
  },
  infoRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  highlightInfo: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 6,
    padding: 8,
    borderRadius: 8,
    borderLeftWidth: 3,
  },
  highlightText: {
    fontSize: 12,
    fontFamily: "Inter-Medium",
    fontWeight: "500",
    flex: 1,
    lineHeight: 16,
  },
  infoItemFullWidth: {
    flex: 1,
    maxWidth: "100%",
  },
  statusBadge: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 20,
    borderWidth: 1,
  },
  statusDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    marginRight: 4,
  },
  statusText: {
    fontSize: 10,
    fontWeight: "700",
    letterSpacing: 0.5,
    fontFamily: "Inter-Bold",
  },
  footerActions: {
    flexDirection: "row",
    alignItems: "center",
  },
  favoriteButton: {
    padding: 8,
    marginRight: 8,
  },
});

export default React.memo(FeaturedOpportunity, (prevProps, nextProps) => {
  return (
    prevProps.opportunity.id === nextProps.opportunity.id &&
    prevProps.opportunity.title === nextProps.opportunity.title
  );
});
